import { CalendarViewLayout } from "../components/Appointments/CalendarViewLayout";
import { ScheduleList } from "../components/Schedule/ScheduleList";
import { ScheduleViewTabs } from "../components/Schedule/ScheduleViewTabs";
import { useScheduleView } from "../hooks/useScheduleView";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppointmentCalendar } from "../components/Appointments/AppointmentCalendar";
import { useQuery } from "@apollo/client";
import LinearProgress from "@material-ui/core/LinearProgress";
import { PullToRefreshBar, usePullToRefresh } from "../components/PullToRefresh";
import Fade from "@material-ui/core/Fade";
import { useAuth0 } from "@auth0/auth0-react";
import { Appointment, Query, QueryGetMyAppointmentsArgs } from "../generated/nest-graphql";
import { complement, contains, filter, indexBy, map, pathOr, pipe, pluck, prop, sortBy, uniq } from "ramda";
import { mapIndexed } from "../lib/functions";
import palette from "google-palette";
import { add, isSameDay, set, sub } from "date-fns";
import { GET_MY_APPOINTMENTS } from "../graphql/queries/getMyAppointments";
import { flow } from "fp-ts/lib/function";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Grow from "@material-ui/core/Grow";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import TodayIcon from "@material-ui/icons/Today";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";

/** Roughly the height of the calendar's own toolbar — past this it has scrolled away. */
const TOOLBAR_SCROLLED_PAST = 96;

const chipStyle: React.CSSProperties = {
  height: 26,
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
  // Pinned rather than left to MUI's size class, so both badges are the same pill
  // whatever else is overriding the Chip's radius.
  borderRadius: 13,
};

const badgeChip: React.CSSProperties = {
  ...chipStyle,
  color: "#e2e8f0",
  // Near-solid, not the old translucent fill: these float over the calendar grid now,
  // and a 22% wash over gridlines is unreadable.
  backgroundColor: "rgba(30, 41, 59, 0.95)",
  border: "1px solid rgba(148, 163, 184, 0.38)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
  // The floating container ignores pointer events so it never blocks the grid.
  pointerEvents: "auto",
};

const Home: React.FC = () => {
  // Mirrors what the calendar's toolbar is showing, so the header can stand in for it.
  const [toolbarState, setToolbarState] = useState<{
    view: string;
    label: React.ReactNode;
    date?: Date;
  } | null>(null);
  const [toolbarGone, setToolbarGone] = useState(false);
  const [view, setView] = useScheduleView();
  const pullState = usePullToRefresh(() => refetch());
  // Log Out used to live on the More tab, which is gone; this is its new home.
  const { logout } = useAuth0();
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [todaySignal, setTodaySignal] = useState(0);
  const stillMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Whether the calendar is showing today. Day is the only calendar view now, so
  // there is no week case left to consider.
  const showingNow = useMemo(() => {
    const date = toolbarState?.date;
    if (!date) return true;
    return isSameDay(date, new Date());
  }, [toolbarState]);

  useEffect(() => {
    const onScroll = () => setToolbarGone(window.scrollY > TOOLBAR_SCROLLED_PAST);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const backToToolbar = useCallback(
    () => window.scrollTo({ top: 0, behavior: stillMotion ? "auto" : "smooth" }),
    [stillMotion]
  );

  const { futureDate, pastDate } = useMemo(() => {
    const defaultDate = set(new Date(), {
      milliseconds: 0,
      seconds: 0,
      minutes: 0,
      hours: 0,
    });
    const futureDate = add(defaultDate, { weeks: 2 });
    const pastDate = sub(defaultDate, { weeks: 1 });
    return { futureDate, pastDate };
  }, []);

  const { data, loading, refetch } = useQuery<Query, QueryGetMyAppointmentsArgs>(GET_MY_APPOINTMENTS, {
    variables: {
      startRange: pastDate,
      endRange: futureDate,
    },
  });
  const { events, technicianColors } = useMemo(() => {
    if (!prop("getMyAppointments", data)) {
      return { events: null, technicianColors: null };
    }
    const events = flow(
      map((appointment: Appointment) => {
        const { isFixed, technician, allDay, subject, endDate, startDate, id, job, timeWindow } = appointment;

        // Dates that control the position/size of the card in the calendar
        const appointmentStart = new Date(startDate);
        const appointmentEnd = new Date(endDate);

        // Time window dates used for displaying informational data about the event
        const timeWindowStart = timeWindow?.startTimeWindow ? new Date(timeWindow.startTimeWindow) : appointmentStart;
        const timeWindowEnd = timeWindow?.endTimeWindow ? new Date(timeWindow.endTimeWindow) : appointmentEnd;

        const title = job?.vehicleInfo?.year
          ? `${job.vehicleInfo.year} ${job.vehicleInfo.make} ${job.vehicleInfo.model}`
          : subject;
        return {
          id,
          appointment,
          isFixed,
          technician,
          technicianId: prop("id", technician),
          resourceTitle: `${prop("firstName", technician)} ${prop("lastName", technician)}`,
          start: appointmentStart,
          end: appointmentEnd,
          timeWindowStart,
          timeWindowEnd,
          job,
          allDay,
          title: title,
          subject,
        };
      }),
      filter(flow(pathOr("", ["job", "status"]), complement(contains("Withdraw"))))
    )(data.getMyAppointments);
    const technicians = pipe(pluck("technician"), uniq, sortBy(prop("id")))(events);
    const colors = palette("mpn65", technicians.length);
    const technicianColors = pipe(
      mapIndexed((technician: any, index: any) => {
        const color = colors[index];
        return { ...technician, color };
      }),
      indexBy(prop("id") as any) as any
    )(technicians);
    return { events, technicianColors };
  }, [data]);
  return (
    <CalendarViewLayout
      appBar={
        // Sticky wrapper rather than a sticky AppBar, so the floating badges can anchor
        // to the bar's bottom edge (top-full) and travel with it — no hard-coded
        // toolbar height, which differs between portrait, landscape and sm+.
        // Opaque: the tabs row has no background of its own, so without this the list
        // scrolls visibly through it. Matches the Parts tab's sticky header.
        <div className="qa-sticky-offset sticky top-0 z-20 bg-gray-900 border-b border-gray-600">
          <AppBar position="static">
            <Toolbar className="flex justify-between">
              <span className="flex items-center gap-2 min-w-0">
                <FontAwesomeIcon icon={faCalendar as any} />
                <Typography variant="h6" noWrap>
                  My Schedule
                </Typography>
              </span>
              <IconButton
                color="inherit"
                edge="end"
                aria-label="Account"
                aria-haspopup="true"
                onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
              >
                <AccountCircleIcon />
              </IconButton>
              {/* A menu rather than a bare logout button: an app-bar icon is easy to
                  catch with a thumb, and signing out mid-job costs a re-auth. */}
              <Menu
                anchorEl={accountMenuAnchor}
                open={!!accountMenuAnchor}
                onClose={() => setAccountMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={async () => {
                    setAccountMenuAnchor(null);
                    await logout({ returnTo: window.location.origin });
                  }}
                >
                  Log Out
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          <ScheduleViewTabs value={view} onChange={setView} />
          <PullToRefreshBar state={pullState} loading={loading} />
          {/* Centred just below the bar, floating over the page. pointer-events-none so
              the empty space either side never swallows a tap meant for the grid. */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 flex items-center gap-1 z-30 pointer-events-none">
            <Grow
              in={!!(view === "calendar" && toolbarGone && toolbarState)}
              timeout={stillMotion ? 0 : 180}
              unmountOnExit
            >
              <Chip
                size="small"
                clickable
                onClick={backToToolbar}
                icon={<ArrowUpwardIcon style={{ fontSize: 15, color: "#e2e8f0" }} />}
                label={`${toolbarState?.label ?? ""}`}
                aria-label="Back to date controls"
                style={badgeChip}
              />
            </Grow>
            {/* One badge at a time — the position indicator takes precedence. */}
            <Grow
              in={view === "calendar" && !showingNow && !!toolbarState && !toolbarGone}
              timeout={stillMotion ? 0 : 180}
              unmountOnExit
            >
              <Chip
                size="small"
                clickable
                onClick={() => setTodaySignal((n) => n + 1)}
                icon={<TodayIcon style={{ fontSize: 15, color: "#e2e8f0" }} />}
                label="Back to today"
                aria-label="Back to today"
                style={badgeChip}
              />
            </Grow>
          </div>
        </div>
      }
    >
      {/* Keyed on the tab so switching views replays the fade, as the Parts tab does. */}
      <Fade in appear key={view} timeout={stillMotion ? 0 : 220}>
        <div>
          {view === "calendar" ? (
            // The calendar needs its events; until they land the progress bar above is
            // the whole story. The list has its own empty state and renders regardless.
            events && (
              <AppointmentCalendar
                events={events}
                technicianColors={technicianColors}
                refetch={refetch}
                onToolbarState={setToolbarState}
                goToTodaySignal={todaySignal}
              />
            )
          ) : (
            <ScheduleList
              appointments={(data?.getMyAppointments ?? []).filter(Boolean) as Appointment[]}
              pullState={pullState}
            />
          )}
        </div>
      </Fade>
    </CalendarViewLayout>
  );
};

export default Home;
